import React from "react";
import { useNavigate } from "react-router-dom";
import type { RecurrencyAttributes } from "../../../types";
import { ActionButton, Avatar, ClientCarCard, Icons } from "../../shared";
import { formatTime } from "../../../utils/dateUtils";

interface RecurrenciesListViewProps {
  recurrencies: RecurrencyAttributes[];
  onEdit: (recurrency: RecurrencyAttributes) => void;
  onDelete: (recurrencyId: number) => void;
  loading?: boolean;
}

function formatCurrency(price: string | number) {
  const numericPrice = typeof price === "string" ? parseFloat(price) : price;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(isNaN(numericPrice) ? 0 : numericPrice);
}

function formatWeeks(weeks: number) {
  return weeks === 1 ? "A cada 1 semana" : `A cada ${weeks} semanas`;
}

function RecurrencyActions({
  recurrency,
  onEdit,
  onDelete,
  loading,
}: {
  recurrency: RecurrencyAttributes;
  onEdit: (recurrency: RecurrencyAttributes) => void;
  onDelete: (recurrencyId: number) => void;
  loading: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <ActionButton
        variant="outline"
        size="sm"
        onClick={() => onEdit(recurrency)}
        icon={<Icons.Edit sx={{ fontSize: 16 }} />}
        disabled={loading}
        className="w-full justify-center"
      >
        Editar
      </ActionButton>
      <ActionButton
        variant="danger"
        size="sm"
        onClick={() => onDelete(recurrency.id)}
        icon={<Icons.Delete sx={{ fontSize: 16 }} />}
        disabled={loading}
        className="w-full justify-center"
      >
        Excluir
      </ActionButton>
    </div>
  );
}

function RecurrencyMeta({ recurrency }: { recurrency: RecurrencyAttributes }) {
  return (
    <>
      {formatWeeks(recurrency.weeks)}
      <br />
      <span className="font-medium text-gray-500">
        {recurrency.startTime ? `${formatTime(recurrency.startTime)} · ` : ""}
        {formatCurrency(recurrency.price)}
      </span>
    </>
  );
}

export const RecurrenciesListView: React.FC<RecurrenciesListViewProps> = ({
  recurrencies,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const navigate = useNavigate();
  const sortedRecurrencies = [...recurrencies].sort((a, b) =>
    (a.client?.name || "").localeCompare(b.client?.name || "", "pt-BR", {
      sensitivity: "base",
    })
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
      {sortedRecurrencies.map((recurrency) => {
        const clientName = recurrency.client?.name || `Cliente #${recurrency.clientId}`;
        const serviceName =
          recurrency.service?.name || `Serviço #${recurrency.serviceId}`;
        const car = recurrency.clientCar;

        if (car?.plate) {
          return (
            <ClientCarCard
              key={recurrency.id}
              car={car}
              clientName={clientName}
              serviceName={serviceName}
              meta={<RecurrencyMeta recurrency={recurrency} />}
              actions={
                <RecurrencyActions
                  recurrency={recurrency}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  loading={loading}
                />
              }
              onClick={() => navigate(`/admin/clients/${recurrency.clientId}`)}
            />
          );
        }

        return (
          <article
            key={recurrency.id}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden text-left hover:shadow-md hover:border-gray-200 dark:hover:border-slate-500 transition-all duration-200"
          >
            <div className="p-4 sm:p-5">
              <button
                type="button"
                onClick={() => navigate(`/admin/clients/${recurrency.clientId}`)}
                className="flex items-start gap-3 w-full text-left min-w-0"
              >
                <Avatar text={clientName} size="md" variant="blue" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug break-words">
                    {clientName}
                  </h3>
                  <p className="mt-0.5 text-sm text-gray-600 break-words">
                    {serviceName}
                  </p>
                </div>
              </button>

              <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                <div className="text-[11px] sm:text-xs text-gray-400 leading-tight">
                  <RecurrencyMeta recurrency={recurrency} />
                </div>
              </div>

              <div className="mt-3" onClick={(event) => event.stopPropagation()}>
                <RecurrencyActions
                  recurrency={recurrency}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  loading={loading}
                />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};
